import moment from 'moment';
import { ChartDataPoint, ChartSeries } from 'components/Charts/types';
import { MutualFundMark } from 'services/hooks/companies';

// ---------------------------------------------------------------------------
// Enums / constants
// ---------------------------------------------------------------------------

export const ViewMode = { Chart: 'chart', Table: 'table' } as const;
export type ViewModeValue = typeof ViewMode[keyof typeof ViewMode];

export const Period = { M3: '3M', M6: '6M', Y1: '1Y', Y2: '2Y', YTD: 'YTD', All: 'ALL' } as const;
export type PeriodValue = typeof Period[keyof typeof Period];
export const PERIODS = Object.values(Period) as PeriodValue[];

export const EndDateMode = { Measurement: 'measurement', Latest: 'latest' } as const;
export type EndDateModeValue = typeof EndDateMode[keyof typeof EndDateMode];

export const FUND_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#7b1fa2', '#0097a7', '#c62828'];

const GRAY_AREA_STYLE = { areaStyle: { color: 'rgba(0, 0, 0, 0.06)' } };

// ---------------------------------------------------------------------------
// Small numeric / string helpers
// ---------------------------------------------------------------------------

export const avgOf = (nums: number[]): number =>
  nums.reduce((a, b) => a + b, 0) / nums.length;

export const weightedAvgOf = (pairs: { value: number; weight: number }[]): number => {
  const totalWeight = pairs.reduce((sum, p) => sum + p.weight, 0);
  if (totalWeight === 0) return pairs.reduce((sum, p) => sum + p.value, 0) / pairs.length;
  return pairs.reduce((sum, p) => sum + p.value * p.weight, 0) / totalWeight;
};

export const round2 = (n: number): number => Math.round(n * 100) / 100;

export const getFundLabel = (mark: MutualFundMark): string => mark.fund_family;

export const toNum = (val: number | string | null): number | null => {
  if (val === null || val === undefined) return null;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? null : n;
};

// ---------------------------------------------------------------------------
// Domain helpers
// ---------------------------------------------------------------------------

/** Returns price per share from the mark directly, or derives it from fair value / shares. */
export const getPricePerShare = (m: MutualFundMark): number | null => {
  const pps = toNum(m.price_per_share);
  if (pps !== null) return pps;
  const fv = toNum(m.fair_value_usd);
  if (fv !== null && m.shares_held && m.shares_held > 0) return fv / m.shares_held;
  return null;
};

/** Maps any reporting date to the last day of its calendar quarter. */
export const getQuarterEnd = (dateStr: string): string => {
  const [year, month] = dateStr.split('-').map(Number);
  if (month <= 3) return `${year}-03-31`;
  if (month <= 6) return `${year}-06-30`;
  if (month <= 9) return `${year}-09-30`;
  return `${year}-12-31`;
};

export const formatCurrency = (val: number | null): string => {
  if (val === null) return '—';
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ---------------------------------------------------------------------------
// Date / period helpers
// ---------------------------------------------------------------------------

const subtractMonths = (dateStr: string, months: number): string =>
  moment(dateStr).subtract(months, 'months').format('YYYY-MM-DD');

/** Returns the ISO start date for a given period relative to endDate, or null for ALL. */
export const getPeriodStartDate = (endDate: string, period: PeriodValue): string | null => {
  switch (period) {
    case Period.M3:  return subtractMonths(endDate, 3);
    case Period.M6:  return subtractMonths(endDate, 6);
    case Period.Y1:  return subtractMonths(endDate, 12);
    case Period.Y2:  return subtractMonths(endDate, 24);
    case Period.YTD: return `${endDate.slice(0, 4)}-01-01`;
    case Period.All: return null;
    default:         return null;
  }
};

// ---------------------------------------------------------------------------
// Chart formatting
// ---------------------------------------------------------------------------

const formatQuarterEnd = (quarterEnd: string): string => {
  const [year, month, day] = quarterEnd.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

const formatIsoDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

/** x-axis label formatter — uses precise dates for a single fund, quarter labels otherwise. */
export const formatXAxisLabel = (v: string | number, singleFund: boolean): string =>
  singleFund ? formatIsoDate(String(v)) : formatQuarterEnd(String(v));

// ---------------------------------------------------------------------------
// Chart data builders
// ---------------------------------------------------------------------------

/**
 * Groups marks into per-fund, per-quarter weighted-average price/share values.
 * Returns Map<fundLabel, Map<quarterEndDate, {pps, shares}>> where pps is the
 * shares-weighted average price per share and shares is the total shares held.
 */
export const buildQuarterlyFundData = (
  marks: MutualFundMark[],
): Map<string, Map<string, { pps: number; shares: number }>> => {
  const raw = new Map<string, Map<string, { sumValue: number; sumShares: number }>>();

  marks.forEach(m => {
    const pps = getPricePerShare(m);
    if (pps === null) return;
    const shares = m.shares_held ?? 0;
    const fund = getFundLabel(m);
    const quarter = getQuarterEnd(m.reporting_date);
    if (!raw.has(fund)) raw.set(fund, new Map());
    const qMap = raw.get(fund)!;
    const prev = qMap.get(quarter) ?? { sumValue: 0, sumShares: 0 };
    qMap.set(quarter, { sumValue: prev.sumValue + pps * shares, sumShares: prev.sumShares + shares });
  });

  const result = new Map<string, Map<string, { pps: number; shares: number }>>();
  raw.forEach((qMap, fund) => {
    const fundMap = new Map<string, { pps: number; shares: number }>();
    qMap.forEach(({ sumValue, sumShares }, quarter) => {
      fundMap.set(quarter, {
        pps: sumShares > 0 ? sumValue / sumShares : 0,
        shares: sumShares,
      });
    });
    result.set(fund, fundMap);
  });

  return result;
};

/** Sorted list of all quarter-end keys present across any fund in the data. */
const allQuartersFrom = (fundData: Map<string, Map<string, { pps: number; shares: number }>>): string[] =>
  [...new Set([...fundData.values()].flatMap(m => [...m.keys()]))].sort();

/** Per-quarter cross-fund shares-weighted average price per share. */
const crossFundAvgForQuarter = (
  fundData: Map<string, Map<string, { pps: number; shares: number }>>,
  quarter: string,
): number | null => {
  const pairs = [...fundData.values()]
    .map(qMap => qMap.get(quarter))
    .filter((v): v is { pps: number; shares: number } => v !== undefined);
  if (!pairs.length) return null;
  return weightedAvgOf(pairs.map(p => ({ value: p.pps, weight: p.shares })));
};

export interface ChartSeriesResult {
  series: ChartSeries[];
  singleFund: boolean;
}

/**
 * Builds chart series from filtered marks.
 * - Single fund: one line with raw reporting dates as x-values.
 * - Multiple funds: avg / max / min lines with quarter-end dates as x-values.
 */
export const buildChartSeries = (marks: MutualFundMark[]): ChartSeriesResult => {
  const uniqueFunds = [...new Set(marks.map(m => getFundLabel(m)))];

  if (uniqueFunds.length === 0) return { series: [], singleFund: false };

  if (uniqueFunds.length === 1) {
    const byDate = new Map<string, { sumValue: number; sumShares: number }>();
    marks.forEach(m => {
      const pps = getPricePerShare(m);
      if (pps === null) return;
      const shares = m.shares_held ?? 0;
      const prev = byDate.get(m.reporting_date) ?? { sumValue: 0, sumShares: 0 };
      byDate.set(m.reporting_date, { sumValue: prev.sumValue + pps * shares, sumShares: prev.sumShares + shares });
    });
    const data: ChartDataPoint[] = [...byDate.keys()].sort().map(date => {
      const { sumValue, sumShares } = byDate.get(date)!;
      return { x: date, y: round2(sumShares > 0 ? sumValue / sumShares : 0) };
    });
    return {
      singleFund: true,
      series: [{ id: 'value', label: uniqueFunds[0], data, color: FUND_COLORS[0], config: GRAY_AREA_STYLE }],
    };
  }

  const fundData = buildQuarterlyFundData(marks);
  const quarters = allQuartersFrom(fundData);

  const avgData: ChartDataPoint[] = [];
  const maxData: ChartDataPoint[] = [];
  const minData: ChartDataPoint[] = [];

  quarters.forEach(q => {
    const pairs = uniqueFunds
      .map(fund => fundData.get(fund)?.get(q))
      .filter((v): v is { pps: number; shares: number } => v !== undefined);
    if (!pairs.length) return;
    avgData.push({ x: q, y: round2(weightedAvgOf(pairs.map(p => ({ value: p.pps, weight: p.shares })))) });
    maxData.push({ x: q, y: Math.max(...pairs.map(p => p.pps)) });
    minData.push({ x: q, y: Math.min(...pairs.map(p => p.pps)) });
  });

  return {
    singleFund: false,
    series: [
      { id: 'avg', label: 'Wtd. Avg', data: avgData, color: FUND_COLORS[0], config: GRAY_AREA_STYLE },
      { id: 'max', label: 'Maximum', data: maxData, color: '#2e7d32' },
      { id: 'min', label: 'Minimum', data: minData, color: '#d32f2f' },
    ],
  };
};

export interface QuarterDetail {
  date: string;
  value: number;
}

export interface HeaderStats {
  headerValue: number | null;
  change: { abs: number; pct: number } | null;
  quarterDetails: QuarterDetail[];
}

/**
 * Computes the header stats from filtered marks:
 * - headerValue: average of all quarterly cross-fund averages in the period
 * - change: movement from the first to the last quarter
 */
export const computeHeaderStats = (marks: MutualFundMark[]): HeaderStats => {
  const fundData = buildQuarterlyFundData(marks);
  const quarters = allQuartersFrom(fundData);

  if (!quarters.length) return { headerValue: null, change: null, quarterDetails: [] };

  const quarterDetails: QuarterDetail[] = quarters
    .map(q => {
      const value = crossFundAvgForQuarter(fundData, q);
      return value !== null ? { date: q, value: round2(value) } : null;
    })
    .filter((v): v is QuarterDetail => v !== null);

  const headerValue = quarterDetails.length ? avgOf(quarterDetails.map(d => d.value)) : null;

  const first = crossFundAvgForQuarter(fundData, quarters[0]);
  const last = crossFundAvgForQuarter(fundData, quarters[quarters.length - 1]);
  const change = first !== null && last !== null && first !== 0
    ? { abs: last - first, pct: ((last - first) / first) * 100 }
    : null;

  return { headerValue, change, quarterDetails };
};
